import React, { Suspense, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import SectionLoader from './components/SectionLoader';
import heroBackground640 from './components/coding-640.webp';
import heroBackground960 from './components/coding-960.webp';
import heroBackground1200 from './components/coding.webp';
import { caseStudyItems } from './data/caseStudies';
import { experienceTimelineItems } from './data/experience';
import { skillKeywords } from './data/skills';
import { scheduleAnalyticsLoad } from './utils/analytics';

const siteMetadata = require('./siteMetadata');

const About = React.lazy(() => import('./components/About'));
const Skills = React.lazy(() => import('./components/Skills'));
const CaseStudies = React.lazy(() => import('./components/CaseStudies'));
const ExperienceTimeline = React.lazy(() => import('./components/ExperienceTimeline'));
const Contact = React.lazy(() => import('./components/Contact'));

const App = () => {
  const siteUrl = (siteMetadata?.siteUrl || 'https://www.rajendrapareek.com').replace(/\/$/, '');
  const SITE_URL = `${siteUrl}/`;
  const META_DESCRIPTION =
    siteMetadata?.description ||
    'Full stack developer in Jodhpur, India specializing in C#, React, and SQL Server, delivering high-performance web applications and integrations.';
  const META_KEYWORDS =
    siteMetadata?.keywords ||
    'Rajendra Pareek, Full Stack Developer, C#, React.js, SQL Server, JavaScript, APIs, Jodhpur, Rajasthan, India, Portfolio';
  const OG_IMAGE_URL = `${SITE_URL}preview.svg`;
  const APPLE_TOUCH_ICON =
    "data:image/svg+xml,%3Csvg%20xmlns%3D'http%3A//www.w3.org/2000/svg'%20viewBox%3D'0%200%20180%20180'%3E%0A%3Cdefs%3E%0A%20%20%3ClinearGradient%20id%3D'grad'%20x1%3D'0%25'%20y1%3D'0%25'%20x2%3D'100%25'%20y2%3D'100%25'%3E%0A%20%20%20%20%3Cstop%20offset%3D'0%25'%20stop-color%3D'%232b6cb0'/%3E%0A%20%20%20%20%3Cstop%20offset%3D'100%25'%20stop-color%3D'%2338b2ac'/%3E%0A%20%20%3C/linearGradient%3E%0A%3C/defs%3E%0A%3Crect%20width%3D'180'%20height%3D'180'%20rx%3D'28'%20fill%3D'url(%23grad)'/%3E%0A%3Ctext%20x%3D'50%25'%20y%3D'55%25'%20font-family%3D%22Inter%2C%20Arial%2C%20sans-serif%22%20font-size%3D'72'%20font-weight%3D'700'%20text-anchor%3D'middle'%20fill%3D'%23ffffff'%3ERP%3C/text%3E%0A%3Ctext%20x%3D'50%25'%20y%3D'73%25'%20font-family%3D%22Inter%2C%20Arial%2C%20sans-serif%22%20font-size%3D'18'%20font-weight%3D'600'%20text-anchor%3D'middle'%20fill%3D'%23e0f2fe'%3EPortfolio%3C/text%3E%0A%3C/svg%3E";
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const educationItem = experienceTimelineItems.find(item => item.type === 'education');
  const educationItemName = educationItem ? educationItem.subtitle : '';
  const personSchema = useMemo(() => {
    const alumniOf = educationItemName
      ? {
          '@type': 'CollegeOrUniversity',
          name: educationItemName
        }
      : null;

    return {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Rajendra Pareek',
      url: SITE_URL,
      jobTitle: 'Full Stack Developer',
      image: OG_IMAGE_URL,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Jodhpur',
        addressRegion: 'Rajasthan',
        addressCountry: 'India'
      },
      knowsAbout: skillKeywords,
      ...(alumniOf ? { alumniOf } : {}),
      sameAs: ['https://www.linkedin.com/in/rpareek347/']
    };
  }, [SITE_URL, OG_IMAGE_URL, educationItemName, skillKeywords]);
  const websiteSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Rajendra Pareek Portfolio',
      url: SITE_URL,
      inLanguage: 'en',
      description: META_DESCRIPTION,
      creator: {
        '@type': 'Person',
        name: 'Rajendra Pareek'
      },
      potentialAction: {
        '@type': 'ContactAction',
        target: `${SITE_URL}#contact`
      }
    }),
    [META_DESCRIPTION, SITE_URL]
  );
  const caseStudySchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Case Studies',
      itemListElement: caseStudyItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Project',
          name: item.title,
          description: item.summary,
          url: `${SITE_URL}#case-study-${item.id}`,
          keywords: item.focusAreas.join(', ')
        }
      }))
    }),
    [SITE_URL, caseStudyItems]
  );
  const experienceSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Work Experience',
      itemListElement: experienceTimelineItems
        .filter(item => item.type === 'work')
        .map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'OrganizationRole',
            roleName: item.title,
            startDate: item.startDate,
            ...(item.endDate ? { endDate: item.endDate } : {}),
            description: item.content.join(' '),
            worksFor: {
              '@type': 'Organization',
              name: item.subtitle
            }
          }
        }))
    }),
    [experienceTimelineItems]
  );
  const structuredData = useMemo(
    () => [
      personSchema,
      websiteSchema,
      caseStudySchema,
      experienceSchema,
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Rajendra Pareek | Full Stack Developer',
        url: SITE_URL,
        inLanguage: 'en',
        description: META_DESCRIPTION,
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: OG_IMAGE_URL,
          contentUrl: OG_IMAGE_URL,
          width: 1200,
          height: 630
        },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: SITE_URL
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Skills & Knowledge',
              item: `${SITE_URL}#skills-knowledge`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Case Studies',
              item: `${SITE_URL}#case-studies`
            },
            {
              '@type': 'ListItem',
              position: 4,
              name: 'Experience & Education',
              item: `${SITE_URL}#experience-education`
            },
            {
              '@type': 'ListItem',
              position: 5,
              name: 'Contact',
              item: `${SITE_URL}#contact`
            }
          ]
        }
      }
    ],
    [META_DESCRIPTION, OG_IMAGE_URL, SITE_URL, personSchema, websiteSchema, caseStudySchema, experienceSchema]
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return undefined;
    }

    return scheduleAnalyticsLoad();
  }, []);

  return (
    <div className="App">
      <Helmet>
        <html lang="en" />
        <title>Rajendra Pareek | Full Stack Developer</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content={META_DESCRIPTION} />
        <meta name="keywords" content={META_KEYWORDS} />
        <meta name="author" content="Rajendra Pareek" />
        <meta name="robots" content="index,follow" />
        <meta name="googlebot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
        <meta name="color-scheme" content="dark" />
        <meta name="format-detection" content="telephone=no,address=no,email=no" />
        <meta name="theme-color" content="#0e172a" />
        <meta name="application-name" content="Rajendra Pareek Portfolio" />
        <meta name="referrer" content="strict-origin-when-cross-origin" />
        <link rel="canonical" href={SITE_URL} />
        <link rel="icon" type="image/svg+xml" href="/rp-icon.svg" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="shortcut icon" href="/favicon.ico" />
        <link
          rel="alternate icon"
          href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%232b6cb0'/%3E%3Cstop offset='100%25' stop-color='%2338b2ac'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='64' height='64' rx='16' fill='url(%23grad)'/%3E%3Cpath d='M20 44V20h12c6.6 0 11 3.6 11 9 0 4.5-3 7.4-7.4 8.2L45 44h-7.5l-7.1-6.8H27V44h-7zM27 32.4h5c2.7 0 4.2-1.2 4.2-3.3 0-2.1-1.5-3.1-4.2-3.1H27v6.4z' fill='%23fff'/%3E%3C/svg%3E"
        />
        <link rel="apple-touch-icon" sizes="180x180" href={APPLE_TOUCH_ICON} />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="preload"
          as="image"
          href={heroBackground1200}
          type="image/webp"
          imageSrcSet={`${heroBackground640} 640w, ${heroBackground960} 960w, ${heroBackground1200} 1200w`}
          imageSizes="(max-width: 640px) 640px, (max-width: 1024px) 960px, 1200px"
        />
        <meta property="og:title" content="Rajendra Pareek | Full Stack Developer" />
        <meta property="og:description" content={META_DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:site_name" content="Rajendra Pareek Portfolio" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:image:secure_url" content={OG_IMAGE_URL} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Preview of Rajendra Pareek's portfolio website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Rajendra Pareek | Full Stack Developer" />
        <meta name="twitter:description" content={META_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
        <meta name="twitter:image:alt" content="Preview of Rajendra Pareek's portfolio website" />
        {structuredData.map(data => {
          const key = `${data['@type']}-${data.name || data.url || data['@id'] || 'schema'}`;
          return (
            <script key={key} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          );
        })}
      </Helmet>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <Navbar />
      <main id="main-content">
        <Hero />
        <Suspense fallback={<SectionLoader label="About section" />}>
          <About />
        </Suspense>
        <Suspense fallback={<SectionLoader label="Skills section" />}>
          <Skills />
        </Suspense>
        <Suspense fallback={<SectionLoader label="Case studies section" />}>
          <CaseStudies />
        </Suspense>
        <Suspense fallback={<SectionLoader label="Experience section" />}>
          <ExperienceTimeline />
        </Suspense>
        <Suspense fallback={<SectionLoader label="Contact section" />}>
          <Contact />
        </Suspense>
      </main>
      <footer className="site-footer">
        <div className="site-footer__inner">
          <p>Copyright {currentYear} Rajendra Pareek. All rights reserved.</p>
          <a className="site-footer__contact-link" href="#contact">
            Contact details
          </a>
        </div>
      </footer>
    </div>
  );
};

export default App;
